import { DateRangeOption } from '@fsp-lib/shared/enums';
import { DateTime, Duration } from 'luxon';

export function fspDayOfWeek(dateTime: DateTime): number {
  return dateTime.weekday === 7 ? 0 : dateTime.weekday;
}

export function fspShortDateTime(
  dateToFormat: DateTime,
  userCulture: string,
  includeTime = true,
  operatorTimeFormat?: '24HR' | 'ST',
): string {
  if (!dateToFormat) {
    return null;
  }
  const date = dateToFormat.toLocaleString(DateTime.DATE_SHORT, {
    locale: userCulture,
  });
  if (includeTime) {
    let format = DateTime.TIME_SIMPLE;
    if (operatorTimeFormat) {
      if (operatorTimeFormat === 'ST') {
        format.hourCycle = 'h12';
      }
      if (operatorTimeFormat === '24HR') {
        format.hourCycle = 'h24';
      }
    }
    return `${date} ${dateToFormat.toLocaleString(format)}`;
  }

  return date;
}

export function getScheduleDateTime(
  dateToFormat: DateTime,
  viewType: string,
  endDate?: DateTime,
): string {
  // dateToFormat would occasionally not have weekData set so this solves that problem
  if (!dateToFormat) {
    return null;
  }
  dateToFormat = DateTime.fromJSDate(dateToFormat.toJSDate());
  const now = DateTime.now();
  if (viewType === 'day') {
    if (
      dateToFormat.day === now.day &&
      dateToFormat.month === now.month &&
      dateToFormat.year === now.year
    ) {
      return `Today, ${dateToFormat.toLocaleString({
        month: 'short',
        day: 'numeric',
      })}`;
    } else {
      return `${dateToFormat.weekdayLong}, ${dateToFormat.toLocaleString({
        month: 'short',
        day: 'numeric',
      })}`;
    }
  } else if (viewType === 'range' || viewType === 'week') {
    return `${dateToFormat.toLocaleString({
      month: 'short',
      day: 'numeric',
    })} - ${endDate?.toLocaleString({
      month: 'short',
      day: 'numeric',
    })}`;
  } else {
    return `${dateToFormat.toLocaleString({
      month: 'long',
    })}`;
  }
}

export function fspToISO(dateTime: DateTime, includeTime = true): string {
  if (!dateTime) {
    return null;
  }
  if (!includeTime) {
    dateTime = zeroOutTime(dateTime);
  } else {
    dateTime = dateTime.set({ second: 0, millisecond: 0 });
  }
  return dateTime.toISO({
    includeOffset: false,
    suppressSeconds: true,
    suppressMilliseconds: true,
  });
}

export function fspUtcToCompanyTimeZone(
  utcDate: string,
  timeZoneBias: number,
  userCulture: string,
): string {
  return DateTime.fromISO(utcDate, { zone: 'utc' })
    .plus({ minutes: timeZoneBias }) // luxon DateTime.plus can be used with negative values
    .setLocale(userCulture)
    .toISO({ includeOffset: false });
}

export function fspUtcToJSDate(
  utcDate: string,
  timeZoneBias: number,
  userCulture: string,
): Date {
  const isoDate = DateTime.fromISO(utcDate, { zone: 'utc' })
    .plus({ minutes: timeZoneBias }) // luxon DateTime.plus can be used with negative values
    .setLocale(userCulture);
  return isoDate.toJSDate();
}

export function getCurrentLocalDate(userCulture: string): DateTime {
  return zeroOutTime(DateTime.now().setLocale(userCulture));
}

export function getCurrentLocalTimeToISO(userCulture: string): string {
  return DateTime.now().setLocale(userCulture).set({ millisecond: 0 }).toISO({
    includeOffset: false,
    suppressMilliseconds: true,
  });
}

export function getEndOfLastMonth(date: DateTime): DateTime {
  const offset = Duration.fromObject({ months: 1 });
  return date.minus(offset).endOf('month');
}

export function getEndOfLastQuarter(date: DateTime): DateTime {
  const offset = Duration.fromObject({ quarters: 1 });
  return date.minus(offset).endOf('quarter');
}

export function getEndOfLastWeek(date: DateTime): DateTime {
  const offset = Duration.fromObject({ weeks: 1 });
  return getEndOfWeek(date).minus(offset);
}

export function getEndOfLastYear(date: DateTime): DateTime {
  const offset = Duration.fromObject({ years: 1 });
  return date.minus(offset).endOf('year');
}

export function getEndOfWeek(date: DateTime): DateTime {
  const offset = Duration.fromObject({ days: 1 });
  return date.plus(offset).endOf('week').minus(offset);
}

export function getStartOfLastWeek(date: DateTime): DateTime {
  const offset = Duration.fromObject({ weeks: 1 });
  return getStartOfWeek(date).minus(offset);
}

export function getStartOfLastMonth(date: DateTime): DateTime {
  const offset = Duration.fromObject({ months: 1 });
  return date.minus(offset).startOf('month');
}

export function getStartOfLastQuarter(date: DateTime): DateTime {
  const offset = Duration.fromObject({ quarters: 1 });
  return date.minus(offset).startOf('quarter');
}

export function getStartOfLastYear(date: DateTime): DateTime {
  const offset = Duration.fromObject({ years: 1 });
  return date.minus(offset).startOf('year');
}

export function getStartOfWeek(date: DateTime): DateTime {
  const offset = Duration.fromObject({ days: 1 });
  return date.plus(offset).startOf('week').minus(offset);
}

export function getTimezoneOffsetString(offsetHours: number): string {
  let offsetString = 'UTC';
  if (offsetHours < 0) {
    offsetString = `UTC${offsetHours}`;
  } else if (offsetHours > 0) {
    offsetString = `UTC+${offsetHours}`;
  }
  return offsetString;
}

export function getOffsetFromDateRangeId(
  dateRangeId: DateRangeOption,
): Duration {
  let offset = Duration.fromObject({ months: 1 });

  switch (dateRangeId) {
    case DateRangeOption.Today:
    case DateRangeOption.Yesterday:
      offset = Duration.fromObject({ days: 1 });
      break;

    case DateRangeOption.ThisWeek:
    case DateRangeOption.LastWeek:
      offset = Duration.fromObject({ weeks: 1 });
      break;

    case DateRangeOption.ThisMonth:
    case DateRangeOption.LastMonth:
      offset = Duration.fromObject({ months: 1 });
      break;

    case DateRangeOption.ThisQuarter:
    case DateRangeOption.LastQuarter:
      offset = Duration.fromObject({ quarters: 1 });
      break;

    case DateRangeOption.ThisYear:
    case DateRangeOption.LastYear:
      offset = Duration.fromObject({ years: 1 });
      break;
  }

  return offset;
}

export function getLuxonDateTime(
  value: string | Date | number | null | undefined,
  userCulture: string,
  includeTime = true,
): DateTime | null {
  let dateToUse: DateTime;
  if (value == null) {
    return null;
  } else if (typeof value === 'string') {
    dateToUse = DateTime.fromISO(value).setLocale(userCulture);
  } else if (typeof value === 'number') {
    dateToUse = DateTime.fromMillis(value).setLocale(userCulture);
  } else {
    dateToUse = DateTime.fromJSDate(value).setLocale(userCulture);
  }
  if (!includeTime) {
    dateToUse = zeroOutTime(dateToUse);
  }
  if (dateToUse.isValid) {
    return dateToUse;
  }

  return null;
}

export function dateTimesAreDifferent(
  originalValue: DateTime,
  newValue: DateTime,
): boolean {
  if ((originalValue && !newValue) || (!originalValue && newValue)) {
    return true;
  }
  const dateDiff = originalValue.diff(newValue).toObject();
  if (dateDiff.milliseconds !== 0) {
    return true;
  }
  return false;
}

export function zeroOutTime(dateTime: DateTime): DateTime {
  return dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
}

export function getNextTimeBasedOnScheduleTimeBlock(
  date: DateTime,
  scheduleTimeBlock: number,
): DateTime {
  if (!date || !scheduleTimeBlock || scheduleTimeBlock === 1) {
    return date;
  }
  const originalMinute = +date.minute;
  const newMinute =
    (Math.ceil(originalMinute / scheduleTimeBlock) * scheduleTimeBlock) % 60;
  let newDate = date.set({ minute: newMinute });
  if (newMinute === 0 && originalMinute !== 0) {
    newDate = newDate.plus({ hours: 1 });
  }
  return newDate;
}
